import { Button, ButtonGroup } from '@mui/material';
import { DataGridPremiumProps, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import dayjs from 'dayjs';
import { IProductWarehouseContract } from 'features/inventory/products/types';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import TenantPage from 'layouts/TenantPage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useIsTechnicianCoordinator } from 'shared/permissions/useIsTechnicianCoordinator';
import { locationService } from 'shared/services';
import { Option } from 'shared/types/general';
import toast from 'shared/utils/toast';
import { movementReasonService } from '../api/movement-reason.service';
import { unapprovedMovementService } from '../api/unapproved-movement.service';
import DetailPanelContent from '../components/DetailPanelContent';
import UnapprovedMovementsUpdateDiaolog from '../components/dialog/UnapprovedMovementsUpdateDiaolog';
import { UnapprovedMovementColumns } from '../unapproved-movements';
import { AggregatedUnapprovedMovementColumns } from '../unapproved-movements-aggregated';

export const UnapprovedMovements = () => {
  const translationPrefix = 'pages.movements.unapproved';
  const { t } = useTranslation();
  const datagridRefresh = useRef(null);
  const [searchParams] = useSearchParams();

  const [locations, setLocations] = useState<Option[]>([]);
  const [movementReasons, setMovementReasons] = useState<Option[]>([]);
  const [pwc, setPwc] = useState<IProductWarehouseContract>(null);
  const selectedMovements = useRef<Record<string, GridRowSelectionModel>>({});
  const [warehouses, setWarehouses] = useState<Option[]>([]);

  const [identificators, setIdentificators] = useState<number[]>([]);
  const [openEditDialog, setOpenEditDialog] = useState(false);

  const { transportDocument } = useParams();
  const noDDT = searchParams.get('noDDT');
  const warehouse = searchParams.get('warehouse');
  const DdtDate = searchParams.get('transport_document_date');

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orderRef = queryParams.get('order_ref');

  useEffect(() => {
    movementReasonService.getAllPaginated().then((res) => {
      setMovementReasons(res.results);
    });
    warehouseService.getAllBaseInformation().then(setWarehouses);
    locationService.getAllBaseInformation().then(setLocations);
  }, []);

  const setOpenDialog = useCallback((row: any) => {
    setIdentificators(row.ids);
    setPwc({
      product: row.product,
      warehouse: row.warehouse,
      contract: row.contract,
      date: row.timestamp && dayjs(row.timestamp).format('DD/MM/YYYY')
    });
    setOpenEditDialog(true);
  }, []);

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const onSubmitEdit = async (values) => {
    unapprovedMovementService.editAggregatedRow(identificators, values.contract).then((res) => {
      setOpenEditDialog(false);
      if (datagridRefresh?.current) datagridRefresh.current();
      toast.success(t(`${translationPrefix}.edit-success`));
    });
  };

  const approveSelectedMovements = () => {
    if (!checkSelectedMovements()) return;

    const movementsToApprove = Object.values(selectedMovements.current)
      .flat()
      .map((id) => +id);
    unapprovedMovementService.approveSelected(movementsToApprove).then((res) => {
      if (res['error']) {
        toast.error(res['error']);
      }
      if (res['warnings']) {
        res['warnings'].forEach((warning) => {
          toast.warning(warning.message);
        });
      }
      if (res['details']) {
        toast.success(res['details']);
      }
      selectedMovements.current = {};
      if (datagridRefresh?.current) datagridRefresh.current();
    });
  };

  const deleteSelectedMovements = () => {
    if (!checkSelectedMovements()) return;
    // TODO: NON CI DOVREBBE ESSERE DIALOG DI CONFERMAAAA???
    const movementsToDelete = Object.values(selectedMovements.current)
      .flat()
      .map((id) => +id);
    unapprovedMovementService.deleteSelected(movementsToDelete).then(() => {
      selectedMovements.current = {};
      if (datagridRefresh?.current) datagridRefresh.current();
    });
  };

  function checkSelectedMovements() {
    if (Object.values(selectedMovements.current).flat().length === 0) {
      alert('Nessun movimento selezionato');
      return false;
    } else {
      return true;
    }
  }

  const setSelectedMovementsCallback = useCallback((key, value) => {
    selectedMovements.current = {
      ...selectedMovements.current,
      [key]: value
    };
  }, []);

  // COLUMNS

  const movementReasonCodes = useMemo(
    () => movementReasons.map((reason) => reason.code),
    [movementReasons]
  );

  const warehouseCodes = useMemo(() => warehouses.map((warehouse) => warehouse.code), [warehouses]);
  const isTechnicianCoordinatorRole = useIsTechnicianCoordinator();

  const aggregatedColumns: GridColDef[] = useMemo(
    () =>
      AggregatedUnapprovedMovementColumns(
        t,
        isTechnicianCoordinatorRole,
        setOpenDialog,
        movementReasonCodes
      ),
    [t, isTechnicianCoordinatorRole, setOpenDialog, movementReasonCodes]
  );

  const columns: GridColDef[] = useMemo(
    () => UnapprovedMovementColumns(t, movementReasonCodes, warehouseCodes, locations),
    [t, movementReasonCodes, warehouseCodes, locations]
  );

  const getDetailPanelContent = useCallback<
    NonNullable<DataGridPremiumProps['getDetailPanelContent']>
  >(
    ({ row }) => (
      <DetailPanelContent
        uuid={row.id}
        service={unapprovedMovementService}
        row={row}
        noDDT={noDDT}
        columns={columns}
        setSelectedMovements={setSelectedMovementsCallback}
      />
    ),
    [columns, noDDT, setSelectedMovementsCallback]
  );

  const outerParams = useMemo(
    () => ({
      ...(noDDT
        ? { noDDT }
        : { transport_document_code: transportDocument, order_ref: orderRef ?? '' }),
      ...(warehouse && { warehouse }),
      ...(DdtDate && { transport_document_date: DdtDate })
    }),
    [noDDT, transportDocument, orderRef, warehouse, DdtDate]
  );

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      menuRight={
        <ButtonGroup>
          <Button onClick={approveSelectedMovements}>
            {t(`${translationPrefix}.bulk-approve`)}
          </Button>
          {/* TODO : CONTROLLO PERMISSI??? */}
          <Button onClick={deleteSelectedMovements}>{t(`${translationPrefix}.bulk-delete`)}</Button>
        </ButtonGroup>
      }>
      <EnhancedDataGrid
        columns={aggregatedColumns}
        service={unapprovedMovementService}
        getMethod={unapprovedMovementService.aggregated}
        warehouseFilterParam="warehouse"
        outerParams={outerParams}
        refresh={datagridRefresh}
        getDetailPanelContent={getDetailPanelContent}
        disableDelete
      />

      <UnapprovedMovementsUpdateDiaolog
        isOpen={openEditDialog}
        onClose={handleCloseEditDialog}
        identificators={identificators}
        movementData={pwc}
        onSubmit={onSubmitEdit}
      />
    </TenantPage>
  );
};

export default UnapprovedMovements;
